import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Search, Instagram, Phone, MoreHorizontal, Pencil, Eye, Archive, ArchiveRestore, Trash2, FileText, ClipboardList, CalendarPlus, AlertTriangle, Flame } from "lucide-react";
import { toast } from "sonner";
import { formatDate, clientName, formatApiError } from "../lib/format";
import { canDelete, canForceDelete, isManagement } from "../lib/constants";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import ForceDeleteModal from "../components/ForceDeleteModal";

const emptyForm = {
  bride_name: "", groom_name: "", whatsapp: "", email: "", address: "",
  instagram: "", lead_source: "", assigned_admin: "", notes: "",
};

const LEAD_SOURCES = ["Instagram", "WhatsApp", "Website", "Referral", "Bridestory", "Walk-in", "Event", "Other"];

export default function Clients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const mgmt = isManagement(user);
  const owner = canDelete(user);
  const canForce = canForceDelete(user);

  const [rows, setRows] = useState([]);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [tab, setTab] = useState("active"); // active | archived | all
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  // Add/Edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // Detail modal
  const [detail, setDetail] = useState(null);

  // Archive modal
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConflict, setDeleteConflict] = useState(null); // {relations}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { archived: tab === "archived" ? "true" : tab === "all" ? "all" : undefined } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return clientName(r).toLowerCase().includes(s) ||
      (r.whatsapp || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.address || "").toLowerCase().includes(s) ||
      (r.instagram || "").toLowerCase().includes(s);
  }), [rows, q]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      bride_name: c.bride_name || "", groom_name: c.groom_name || "",
      whatsapp: c.whatsapp || "", email: c.email || "", address: c.address || "",
      instagram: c.instagram || "", lead_source: c.lead_source || "",
      assigned_admin: c.assigned_admin || "", notes: c.notes || "",
    });
    setFormOpen(true);
  };

  const submit = async () => {
    if (!form.bride_name.trim()) { toast.error("Nama mempelai wanita wajib diisi"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Klien diperbarui");
      } else {
        await api.post("/clients", form);
        toast.success("Klien berhasil ditambahkan");
      }
      setFormOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/clients/${c.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      await api.post(`/clients/${archiveTarget.id}/archive`, { reason: archiveReason.trim() });
      toast.success("Klien diarsipkan");
      setArchiveTarget(null); setArchiveReason(""); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doRestore = async (c) => {
    try {
      await api.post(`/clients/${c.id}/restore`);
      toast.success("Klien dipulihkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDelete = async (c) => {
    // Pre-check relations to decide flow (archive vs delete)
    try {
      const { data: rel } = await api.get(`/clients/${c.id}/relations`);
      if (rel.total > 0) {
        setDeleteConflict({ client: c, relations: rel });
        return;
      }
      setDeleteTarget(c);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doDelete = async ({ reason }) => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/clients/${deleteTarget.id}`, { headers: reason ? { "X-Delete-Reason": reason } : {} });
      toast.success("Klien dihapus permanen");
      setDeleteTarget(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.relations) {
        setDeleteTarget(null);
        setDeleteConflict({ client: deleteTarget, relations: d.relations });
      } else {
        toast.error(formatApiError(d));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Master Klien</h1>
          <p className="text-sm text-[#5C6066] mt-1">Database klien tunggal untuk semua divisi. Arsip melindungi histori.</p>
        </div>
        {mgmt && (
          <Button className="bg-brand text-white" data-testid="btn-add-client" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5" /> Tambah Client
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab} data-testid="clients-tabs">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">Aktif</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Diarsipkan</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari nama, WhatsApp, email, IG..." value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="client-search" />
        </div>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Nama Pasangan</th>
                <th className="text-left px-4 py-3">Kontak</th>
                <th className="text-left px-4 py-3">Sumber</th>
                <th className="text-left px-4 py-3">Admin PIC</th>
                <th className="text-center px-4 py-3">Booking</th>
                <th className="text-left px-4 py-3">Dibuat</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="text-center py-10 text-[#8C9096]">Memuat...</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[#8C9096]">Belum ada klien.</td></tr>
              )}
              {!loading && filtered.map((c) => (
                <tr key={c.id} className={`border-t border-[#F0EBE0] hover:bg-[#F7F4EE] ${c.archived ? "opacity-70" : ""}`} data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button className="font-medium text-[#1E2022] hover:text-brand text-left" onClick={() => openDetail(c)} data-testid={`client-name-${c.id}`}>
                        {clientName(c)}
                      </button>
                      {c.archived && <Badge variant="outline" className="text-[10px] uppercase border-amber-300 text-amber-800 bg-amber-50">Arsip</Badge>}
                    </div>
                    <div className="text-[11px] text-[#8C9096]">{c.address || "-"}</div>
                  </td>
                  <td className="px-4 py-3">
                    {c.whatsapp && <div className="flex items-center gap-1.5 text-[#5C6066]"><Phone className="w-3 h-3" />{c.whatsapp}</div>}
                    {c.instagram && <div className="flex items-center gap-1.5 text-[#5C6066] text-xs mt-0.5"><Instagram className="w-3 h-3" />{c.instagram}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#5C6066]">{c.lead_source || "-"}</td>
                  <td className="px-4 py-3 text-[#5C6066]">{c.assigned_admin || "-"}</td>
                  <td className="px-4 py-3 text-center font-semibold">{c.booking_count}</td>
                  <td className="px-4 py-3 text-[#8C9096] text-xs">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <RowActions
                      c={c} mgmt={mgmt} owner={owner} canForce={canForce}
                      onView={() => openDetail(c)} onEdit={() => openEdit(c)}
                      onArchive={() => setArchiveTarget(c)} onRestore={() => doRestore(c)}
                      onDelete={() => openDelete(c)}
                      onForceDelete={() => setForceDeleteTarget(c)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {loading && <div className="text-center py-10 text-[#8C9096] text-sm">Memuat...</div>}
          {!loading && filtered.length === 0 && <div className="text-center py-10 text-[#8C9096] text-sm">Belum ada klien.</div>}
          {!loading && filtered.map((c) => (
            <div key={c.id} className="p-4 flex items-start justify-between gap-3" data-testid={`client-card-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <button className="font-medium hover:text-brand text-left truncate" onClick={() => openDetail(c)}>{clientName(c)}</button>
                  {c.archived && <Badge variant="outline" className="text-[10px] uppercase border-amber-300 text-amber-800 bg-amber-50 flex-shrink-0">Arsip</Badge>}
                </div>
                <div className="text-[11px] text-[#8C9096] mt-0.5">
                  {c.whatsapp || "—"} • {c.lead_source || "—"} • {c.booking_count} booking
                </div>
              </div>
              <RowActions
                c={c} mgmt={mgmt} owner={owner} canForce={canForce}
                onView={() => openDetail(c)} onEdit={() => openEdit(c)}
                onArchive={() => setArchiveTarget(c)} onRestore={() => doRestore(c)}
                onDelete={() => openDelete(c)}
                onForceDelete={() => setForceDeleteTarget(c)}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Client" : "Tambah Client Baru"}</DialogTitle>
            <DialogDescription>Isi data pasangan mempelai. Nama mempelai wanita wajib diisi.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Nama Mempelai Wanita *" k="bride_name" form={form} setForm={setForm} />
            <Field label="Nama Mempelai Pria" k="groom_name" form={form} setForm={setForm} />
            <Field label="No. WhatsApp" k="whatsapp" form={form} setForm={setForm} placeholder="+62..." />
            <Field label="Email" k="email" form={form} setForm={setForm} />
            <Field label="Instagram" k="instagram" form={form} setForm={setForm} placeholder="@handle" />
            <div>
              <Label>Sumber Lead</Label>
              <select className="w-full mt-1.5 h-10 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm"
                value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} data-testid="client-lead_source">
                <option value="">Pilih...</option>
                {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Field label="Admin PIC" k="assigned_admin" form={form} setForm={setForm} />
            <Field label="Alamat" k="address" form={form} setForm={setForm} />
            <div className="sm:col-span-2">
              <Label>Catatan</Label>
              <Textarea className="mt-1.5" rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={busy || !form.bride_name.trim()} className="bg-brand text-white" data-testid="btn-save-client">
              {busy ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Simpan Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="client-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {clientName(detail)}
                  {detail.archived && <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">Arsip</Badge>}
                </DialogTitle>
                <DialogDescription>
                  Dibuat {formatDate(detail.created_at)} oleh {detail.created_by_name || detail.created_by || "-"}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="WhatsApp" value={detail.whatsapp} />
                <Info label="Email" value={detail.email} />
                <Info label="Instagram" value={detail.instagram} />
                <Info label="Sumber" value={detail.lead_source} />
                <Info label="Admin PIC" value={detail.assigned_admin} />
                <Info label="Alamat" value={detail.address} />
                {detail.notes && <div className="col-span-2"><Info label="Catatan" value={detail.notes} /></div>}
              </div>
              <div className="border-t border-[#F0EBE0] pt-3 mt-2">
                <div className="text-xs uppercase text-[#8C9096] mb-2">Ringkasan Histori</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  {["bookings","inquiries","quotations","invoices","payments","tasks"].map((k) => (
                    <div key={k} className="p-2 bg-[#FAF6EE] rounded border border-[#E5E0D8]">
                      <div className="font-mono font-semibold text-sm">{detail.relations?.[k] ?? 0}</div>
                      <div className="text-[10px] uppercase text-[#8C9096]">{k}</div>
                    </div>
                  ))}
                </div>
              </div>
              {mgmt && !detail.archived && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setDetail(null); nav(`/inquiries?client_id=${detail.id}`); }} data-testid="detail-new-inquiry">
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Buat Inquiry
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDetail(null); nav(`/quotations?client_id=${detail.id}`); }} data-testid="detail-new-quotation">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Buat Quotation
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDetail(null); nav(`/bookings?client_id=${detail.id}`); }} data-testid="detail-new-booking">
                    <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Buat Booking
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDetail(null); nav(`/clients/${detail.id}/statement`); }} data-testid="detail-history">
                    <Eye className="w-3.5 h-3.5 mr-1" /> Lihat Statement
                  </Button>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
                {mgmt && !detail.archived && (
                  <Button onClick={() => { openEdit(detail); setDetail(null); }} className="bg-brand text-white">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirm */}
      <Dialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)}>
        <DialogContent data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="w-4 h-4"/> Arsipkan Client</DialogTitle>
            <DialogDescription>Client tidak akan muncul di daftar aktif. Semua histori booking dan finansial tetap tersimpan dan dapat dipulihkan kapan saja.</DialogDescription>
          </DialogHeader>
          {archiveTarget && <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{clientName(archiveTarget)}</div>}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Alasan (opsional)</Label>
            <Textarea rows={2} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} className="mt-1.5" data-testid="archive-reason"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>Batal</Button>
            <Button onClick={doArchive} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="archive-confirm-btn">
              {busy ? "Mengarsipkan..." : "Arsipkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDeleteModal
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Hapus Client Permanen"
        message="Client ini tidak memiliki histori terkait. Aksi ini akan menghapus data secara permanen (bisa dipulihkan lewat Recycle Bin selama 30 hari)."
        itemLabel={deleteTarget ? clientName(deleteTarget) : ""}
        onConfirm={doDelete}
      />

      {/* Force Delete (owner only) */}
      <ForceDeleteModal
        open={!!forceDeleteTarget}
        onOpenChange={(v) => !v && setForceDeleteTarget(null)}
        entityType="client"
        entityId={forceDeleteTarget?.id}
        entityLabel={forceDeleteTarget ? clientName(forceDeleteTarget) : ""}
        onDone={() => { setForceDeleteTarget(null); load(); }}
      />

      {/* Conflict — cannot delete, force archive */}
      <Dialog open={!!deleteConflict} onOpenChange={(v) => !v && setDeleteConflict(null)}>
        <DialogContent data-testid="delete-conflict-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5"/> Tidak dapat dihapus permanen</DialogTitle>
            <DialogDescription>Data ini memiliki histori dan tidak dapat dihapus permanen. Arsipkan/nonaktifkan saja.</DialogDescription>
          </DialogHeader>
          {deleteConflict && (
            <div className="space-y-3">
              <div className="p-2 bg-[#FAF6EE] border border-[#E5E0D8] rounded text-sm font-medium">{clientName(deleteConflict.client)}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(deleteConflict.relations).filter(([k]) => k !== "total").map(([k, v]) => (
                  <div key={k} className="p-2 bg-amber-50 border border-amber-200 rounded">
                    <div className="font-mono font-semibold">{v}</div>
                    <div className="text-[10px] uppercase text-amber-800">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConflict(null)}>Tutup</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="conflict-archive-btn"
              onClick={() => { setArchiveTarget(deleteConflict.client); setDeleteConflict(null); }}>
              <Archive className="w-3.5 h-3.5 mr-1"/> Arsipkan Saja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, k, form, setForm, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        data-testid={`client-${k}`} placeholder={placeholder} />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[#8C9096]">{label}</div>
      <div className="mt-0.5 text-[#1E2022]">{value || "—"}</div>
    </div>
  );
}

function RowActions({ c, mgmt, owner, canForce, onView, onEdit, onArchive, onRestore, onDelete, onForceDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`client-actions-${c.id}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={onView} data-testid={`action-view-${c.id}`}>
          <Eye className="w-3.5 h-3.5 mr-2"/> Lihat
        </DropdownMenuItem>
        {mgmt && !c.archived && (
          <DropdownMenuItem onClick={onEdit} data-testid={`action-edit-${c.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-2"/> Edit
          </DropdownMenuItem>
        )}
        {mgmt && !c.archived && (
          <DropdownMenuItem onClick={onArchive} data-testid={`action-archive-${c.id}`}>
            <Archive className="w-3.5 h-3.5 mr-2"/> Arsipkan
          </DropdownMenuItem>
        )}
        {mgmt && c.archived && (
          <DropdownMenuItem onClick={onRestore} data-testid={`action-restore-${c.id}`}>
            <ArchiveRestore className="w-3.5 h-3.5 mr-2"/> Pulihkan
          </DropdownMenuItem>
        )}
        {owner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-rose-700 focus:text-rose-800 focus:bg-rose-50" data-testid={`action-delete-${c.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-2"/> Hapus Permanen
            </DropdownMenuItem>
          </>
        )}
        {canForce && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onForceDelete} className="text-rose-900 focus:text-rose-900 focus:bg-rose-100 font-semibold" data-testid={`action-force-delete-${c.id}`}>
              <Flame className="w-3.5 h-3.5 mr-2"/> Force Delete Permanen
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
